// bd-deepcontext-b4zw — the scheduled-jobs endpoint group, lifted verbatim out
// of ScheduledJobs.tsx. Stays local to this feature folder (out of
// services/api/) because the bead explicitly excludes api.ts changes.
import { api } from "../../contexts/AuthContext";
import type { BlueprintUsageMap } from "../PromptMultiSelect";
import type { ScheduledJobRunsAggregate } from "../jobRuns/summary";
import type { ScheduledJobRun } from "../jobRuns/types";
import type {
  LocalModelServer,
  ScheduledJob,
  ScheduledJobCreateRequest,
  ScheduledJobStats,
  ScheduledJobUpdateRequest,
  UnmatchedSort,
  UnmatchedUrl,
  UrlPoolEstimate,
} from "./types";

export const scheduledJobsApi = {
  async getJobs(): Promise<ScheduledJob[]> {
    const response = await api.get("/api/scheduled-jobs");
    return response.data;
  },

  async createJob(request: ScheduledJobCreateRequest): Promise<ScheduledJob> {
    const response = await api.post("/api/scheduled-jobs", request);
    return response.data;
  },

  async updateJob(
    id: string,
    updates: ScheduledJobUpdateRequest,
  ): Promise<ScheduledJob> {
    const response = await api.put(`/api/scheduled-jobs/${id}`, updates);
    return response.data;
  },

  async deleteJob(id: string): Promise<void> {
    await api.delete(`/api/scheduled-jobs/${id}`);
  },

  async executeJob(id: string): Promise<ScheduledJobRun> {
    const response = await api.post(`/api/scheduled-jobs/${id}/execute`);
    return response.data;
  },

  async getJobRuns(id: string): Promise<ScheduledJobRun[]> {
    const response = await api.get(`/api/scheduled-jobs/${id}/runs`);
    return response.data;
  },

  // bd-deepcontext-hfv7u.6 — cursor-paginated runs (latest-first). Sending the
  // `cursor` param (even empty for page 1) opts into the `{ runs, nextCursor }`
  // envelope hfv7u.1 added; bare `getJobRuns` still returns the legacy array.
  async getJobRunsPage(
    id: string,
    cursor?: string,
    limit = 50,
  ): Promise<{ runs: ScheduledJobRun[]; nextCursor: string | null }> {
    const response = await api.get(`/api/scheduled-jobs/${id}/runs`, {
      params: { cursor: cursor ?? "", limit },
    });
    return response.data;
  },

  // bd-deepcontext-hfv7u.7 — per-job aggregate over the full run history
  // (counts + avg match-rate/duration). Powers the modal's KPI strip so it is
  // correct beyond the fetched window, not a client-side reduce.
  async getJobRunsAggregate(id: string): Promise<ScheduledJobRunsAggregate> {
    const response = await api.get(`/api/scheduled-jobs/${id}/runs/aggregate`);
    return response.data;
  },

  // deepcontext-63k8.3 — self-hosted servers the form's model picker offers.
  // 403 for everyone but the allow-listed user; callers gate on that first.
  async getLocalModels(): Promise<LocalModelServer[]> {
    const response = await api.get("/api/scheduled-jobs/local-models");
    return response.data;
  },

  async getStats(): Promise<ScheduledJobStats> {
    const response = await api.get("/api/scheduled-jobs/stats");
    return response.data;
  },

  async addUrls(id: string, urls: string[]): Promise<void> {
    await api.post(`/api/scheduled-jobs/${id}/urls`, { urls });
  },

  async cancelJobRun(jobId: string, runId: string): Promise<void> {
    const response = await api.post(
      `/api/scheduled-jobs/${jobId}/runs/${runId}/cancel`,
    );
    return response.data;
  },

  async getRunUnmatchedUrls(
    jobId: string,
    runId: string,
    sort: UnmatchedSort,
  ): Promise<{
    runId: string;
    sort: UnmatchedSort;
    urls: UnmatchedUrl[];
  }> {
    const response = await api.get(
      `/api/scheduled-jobs/${jobId}/runs/${runId}/unmatched-urls`,
      { params: { sort } },
    );
    return response.data;
  },

  async estimateUrlPool(
    params: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<UrlPoolEstimate> {
    const response = await api.post(
      "/api/scheduled-jobs/estimate-url-pool",
      params,
      { signal },
    );
    return response.data;
  },

  // bd-n3yqb — which job (if any) owns each blueprint. Omit promptIds to fetch
  // usage across every blueprint. Used to disable already-used blueprints in
  // the picker and name the owning job.
  async getBlueprintUsage(): Promise<BlueprintUsageMap> {
    const response = await api.get("/api/scheduled-jobs/blueprint-usage");
    return response.data;
  },
};
